// Exports an e-Jagriti (Consumer Court) case to Firebase, mirroring the High
// Court export (case-limit gating, duplicate check, calendar reminders).
// e-Jagriti PDFs are served via our stable proxy, so unlike High Court we don't
// persist PDFs to Storage — the order rows keep their portal URLs and are opened
// through the proxy on view.

import { db } from "../firebase";
import { collection, addDoc, query, where, getDocs, serverTimestamp } from "firebase/firestore";
import featureGatingService from "./featureGatingService";

const slash = (d) => (d ? String(d).replace(/-/g, "/") : "");

const deriveStatus = (summary) =>
  /dispos/i.test(summary?.caseStage || "") ? "disposed" : "pending";

function buildCaseData(statusData, historyData, user, subscriptionMetadata) {
  const summary = statusData?.summary || {};
  const historyAndOrders = statusData?.historyAndOrders || [];
  const timeline = historyData?.timeline || [];
  const caseStatus = deriveStatus(summary);
  const previousHearingDate = historyAndOrders[0]?.dateOfHearing ? slash(historyAndOrders[0].dateOfHearing) : "";

  return {
    caseNo: summary.caseNumber || "",
    cino: summary.referenceNumber || "",
    filingNumber: "",
    caseType: "Consumer Court",
    caseFiledDate: slash(summary.filingDate),
    nextHearingDate: slash(summary.nextHearing),
    previousHearingDate,
    courtName: summary.filedIn ? `Consumer Court (${summary.filedIn})` : "Consumer Court (e-Jagriti)",
    petitionerName: summary.complainant || "",
    respondentName: summary.oppositeParty || "",
    allPetitioners: summary.complainant ? [summary.complainant] : [],
    allRespondents: summary.oppositeParty ? [summary.oppositeParty] : [],
    partyContactNo: "",
    petitionerAdvocate: "",
    respondentAdvocate: "",
    allPetitionerAdvocates: [],
    allRespondentAdvocates: [],
    team: [user.uid],
    files: [],
    casenotes: "",
    status: caseStatus,
    owner: user.uid,
    caseHistory: [],
    orders: [],
    payload: {
      caseexportedfrom: "ConsumerCourt",
      caseNumber: summary.caseNumber || "",
      referenceNumber: summary.referenceNumber || "",
      caseStage: summary.caseStage || "",
      filedIn: summary.filedIn || "",
      filedBy: summary.filedBy || "",
      purpose: summary.purpose || "",
      applicationFiled: summary.applicationFiled || [],
      attachedLowerCourt: summary.attachedLowerCourt || "",
      historyAndOrders, // feeds the "History & Orders" tab in CaseDetails
      timeline, // feeds the "Case History" zigzag timeline tab in CaseDetails
      exportedAt: new Date().toISOString(),
    },
    ...(subscriptionMetadata && { subscriptionMetadata }),
    timestamp: serverTimestamp(),
  };
}

/** Subscription case-limit gate (same as High Court). */
export async function checkConsumerCaseLimit(globalUser, statusData) {
  if (!globalUser?.uid) return { canProceed: false };
  const caseStatus = deriveStatus(statusData?.summary);
  const targetCollection = caseStatus === "disposed" ? "disposed" : "pending";
  return featureGatingService.checkCaseLimitBeforeExport(globalUser.uid, 1, targetCollection);
}

/** Returns { collection, docId, caseNo } if the case is already saved, else null. */
export async function findDuplicateConsumerCase(caseNo, uid) {
  if (!caseNo) return null;
  for (const c of ["pending", "disposed", "decided"]) {
    const snap = await getDocs(
      query(collection(db, c), where("caseNo", "==", caseNo), where("owner", "==", uid))
    );
    if (!snap.empty) return { collection: c, docId: snap.docs[0].id, caseNo };
  }
  return null;
}

/** Writes the case + hearing reminders. Returns { caseId, caseStatus }. */
export async function exportConsumerCase({ statusData, historyData, user, userMobile }) {
  const summary = statusData?.summary || {};
  const caseStatus = deriveStatus(summary);

  let subscriptionMetadata = null;
  if (caseStatus === "pending") {
    const pendingSnap = await getDocs(query(collection(db, "pending"), where("owner", "==", user.uid)));
    subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(user.uid, pendingSnap.size);
  }

  const caseData = buildCaseData(statusData, historyData, user, subscriptionMetadata);
  const caseRef = await addDoc(collection(db, caseStatus), caseData);

  if (caseData.nextHearingDate) {
    try {
      await createHearingReminders({
        nextHearingDate: caseData.nextHearingDate,
        caseNo: caseData.caseNo,
        petitionerName: caseData.petitionerName,
        respondentName: caseData.respondentName,
        caseStatus,
        caseRefId: caseRef.id,
        user,
        userMobile,
      });
    } catch (err) {
      console.error("Consumer reminder setup error:", err);
    }
  }

  return { caseId: caseRef.id, caseStatus };
}

async function createHearingReminders({ nextHearingDate, caseNo, petitionerName, respondentName, caseStatus, caseRefId, user, userMobile }) {
  const eventDate = new Date(nextHearingDate.split("/").reverse().join("-"));
  if (isNaN(eventDate.getTime())) return;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const hearingDay = new Date(eventDate); hearingDay.setHours(0, 0, 0, 0);
  if (hearingDay < today) return;

  const now = new Date();
  const reminderDate = new Date(eventDate); reminderDate.setHours(8, 0, 0, 0);
  const eventTimeIST = new Date(eventDate); eventTimeIST.setHours(18, 0, 0, 0);

  if (reminderDate > now) {
    const ref = await addDoc(collection(db, "lawyers", user.uid, "events"), {
      "Case No": caseNo, date: reminderDate.toISOString().split("T")[0], id: Math.floor(Math.random() * 90000) + 10000,
      mobile: userMobile || null, time: "08:00 AM", time1: reminderDate.getTime(),
      title: `${petitionerName} VS ${respondentName}`, description: `Hearing Reminder : Case No. - ${caseNo}`,
      isScheduled: true, status: caseStatus, eventType: "reminder",
    });
    await addDoc(collection(db, "eventReminders"), {
      eventId: ref.id, reminderTime: reminderDate, eventTitle: `Hearing Reminder : Case No. - ${caseNo}`,
      caseNo, caseId: caseRefId, collection: caseStatus, status: "pending", createdAt: new Date(),
      scheduledBy: user.uid, recipientId: user.uid,
    });
  }

  if (eventTimeIST > now) {
    const ref = await addDoc(collection(db, "lawyers", user.uid, "events"), {
      "Case No": caseNo, date: eventDate.toISOString().split("T")[0], id: Math.floor(Math.random() * 90000) + 10000,
      mobile: userMobile || null, time: "06:00 PM", time1: eventTimeIST.getTime(),
      title: `Update Next Hearing: ${petitionerName} VS ${respondentName}`, description: `Case No. - ${caseNo}`,
      isScheduled: true, status: caseStatus, eventType: "updateReminder", isHidden: true,
    });
    await addDoc(collection(db, "eventReminders"), {
      eventId: ref.id, reminderTime: eventTimeIST, eventTitle: `Update Next Hearing : ${caseNo}`,
      caseNo, caseId: caseRefId, collection: caseStatus, status: "pending", createdAt: new Date(),
      scheduledBy: user.uid, recipientId: user.uid,
    });
  }
}
